<script setup>
import { useShortLink } from '@frontJs/composables/shortLink';
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import LinkIcon from '@iconify-vue/ph/link';
import { router } from '@inertiajs/vue3';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import FilePondField from '@js/components/fields/FilePondField.vue';
import InputField from '@js/components/fields/InputField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormLegend from '@js/components/forms/FormLegend.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import { useRoute } from '@js/utils/route';
import { isHttpUrl } from '@js/utils/string';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { object, string } from 'yup';

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    smoPost: {
        type: Object,
        required: false,
    },
    defaultScheduledAt: {
        type: String,
        required: false,
    },
    isPlatformConfigured: {
        type: Boolean,
        required: false,
    },
    maxContentLength: {
        type: Number,
        required: true,
    },
    maxMediaCount: {
        type: Number,
        required: true,
    },
    defaultScheduledDate: {
        type: String,
        required: false,
    },
    defaultPublicationTime: {
        type: String,
        required: true,
    },
    previousSmoPostId: {
        type: Number,
        required: false,
    },
    nextSmoPostId: {
        type: Number,
        required: false,
    },
});

const { route } = useRoute();

const { isShortening, shortenFieldsUrls } = useShortLink();

const formRef = ref(null);

const isSubmitting = ref(false);

const isUploading = ref(false);

/**
 * The server emits `YYYY-MM-DD HH:mm:ss`, which the `date` and `time` fields take apart. Keeping
 * them separate is what lets the time be proposed without imposing a date along with it.
 */
const scheduledAt = props.smoPost?.scheduledAt ?? props.defaultScheduledAt;

const form = reactive({
    content: props.smoPost?.content ?? '',
    linkUrl: props.smoPost
        ? (props.smoPost.linkUrl ?? '')
        : (props.tenant.url ?? ''),
    scheduledDate: scheduledAt
        ? scheduledAt.slice(0, 10)
        : (props.defaultScheduledDate ?? ''),
    scheduledTime: scheduledAt
        ? scheduledAt.slice(11, 16)
        : props.defaultPublicationTime,
    medias: [],
});

const validationSchema = computed(() =>
    object({
        content: string()
            .required('Ce champ est obligatoire.')
            .max(
                props.maxContentLength,
                `Le contenu ne peut pas dépasser ${props.maxContentLength} caractères.`,
            ),
        linkUrl: string().url('Veuillez saisir une URL valide.').nullable(),
        scheduledDate: string().required('Ce champ est obligatoire.'),
        scheduledTime: string().required('Ce champ est obligatoire.'),
    }),
);

const mediaUrlImportEndpoint = route(
    'front.projectManagement.smo.postMediaFromUrl',
);

const initialMedias = (props.smoPost?.medias ?? []).map((media) => ({
    id: media.id,
    url: media.url,
}));

const initialMediaIds = new Set(initialMedias.map((media) => media.id));

const remainingMediaCount = computed(() => {
    const keptCount = form.medias.filter((media) =>
        initialMediaIds.has(media),
    ).length;

    return Math.max(0, props.maxMediaCount - keptCount);
});

/**
 * Shortens the addresses of the post — content and link alike — in a single call: a post is
 * measured in characters, and a tracking URL alone can eat a third of the allowance.
 */
function shortenLinks() {
    return shortenFieldsUrls([
        {
            getValue: () => form.content,
            setValue: (value) => (form.content = value),
        },
        {
            getValue: () => form.linkUrl,
            setValue: (value) => (form.linkUrl = value),
        },
    ]);
}

function save(status) {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.smo.postPost', {
                tenant: props.tenant.id,
                smoPost: props.smoPost?.id,
            }),
            {
                content: form.content,
                linkUrl: form.linkUrl || null,
                scheduledAt: `${form.scheduledDate} ${form.scheduledTime}`,
                status: status,
                medias: form.medias,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.smo.posts', {
                    tenant: props.tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}

function submit() {
    save('scheduled');
}

/**
 * Plain navigation, nothing is saved: the user confirms before losing the changes made here.
 */
function goToSmoPost(smoPostId) {
    if (
        formRef.value?.getMeta()?.touched &&
        !confirm(
            'Voulez-vous vraiment quitter sans sauvegarder les modifications ?',
        )
    ) {
        return;
    }

    router.visit(
        route('front.projectManagement.smo.post', {
            tenant: props.tenant.id,
            smoPost: smoPostId,
        }),
    );
}
</script>

<template>
    <FormRoot
        ref="formRef"
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <AlertBox v-if="!props.isPlatformConfigured" type="warning">
                Le service externe Facebook n'est pas configuré pour ce client :
                la publication ne pourra pas être enregistrée.
            </AlertBox>

            <AlertBox v-if="props.smoPost?.isRemotelyScheduled" type="primary">
                Cette publication est déjà déposée dans le planificateur de
                Facebook. Le contenu et l'heure y seront simplement mis à jour ;
                en revanche, modifier les visuels ou le lien impose de l'y
                retirer puis de l'y redéposer — ce qui est fait automatiquement.
            </AlertBox>

            <FormContent>
                <TextareaField
                    v-model="form.content"
                    label="Contenu"
                    placeholder="Contenu de la publication"
                    field-name="content"
                    field-id="content"
                    :rows="8"
                    :highlight="[/(#[\p{L}\p{N}_]+)/gu, /(@[\w.]+)/g]"
                    emoji-picker
                    required
                    :legend="`${form.content.length} / ${props.maxContentLength} caractères`"
                >
                    <template #bottom-left-actions>
                        <ActionButton
                            size="small"
                            type="secondary"
                            :left-icon="LinkIcon"
                            :loading="isShortening"
                            :callback="shortenLinks"
                        >
                            Raccourcir les liens
                        </ActionButton>
                    </template>
                </TextareaField>

                <FormGrid :columns="3">
                    <InputField
                        v-model="form.scheduledDate"
                        label="Date de publication"
                        field-name="scheduledDate"
                        field-id="scheduledDate"
                        type="date"
                        required
                    />

                    <InputField
                        v-model="form.scheduledTime"
                        label="Heure de publication"
                        field-name="scheduledTime"
                        field-id="scheduledTime"
                        type="time"
                        required
                    />

                    <InputField
                        v-model="form.linkUrl"
                        label="Lien"
                        placeholder="https://..."
                        field-name="linkUrl"
                        field-id="linkUrl"
                        legend="Optionnel, affiché en aperçu par Facebook"
                    >
                        <template #inputSuffix>
                            <ActionButton
                                size="small"
                                square
                                type="transparent"
                                :icon="ArrowSquareOutIcon"
                                title="Ouvrir le lien dans un nouvel onglet"
                                :href="isHttpUrl(form.linkUrl) ? form.linkUrl : undefined"
                                target="_blank"
                                :disabled="!isHttpUrl(form.linkUrl)"
                            />
                        </template>
                    </InputField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormTitle>Visuels</FormTitle>

                <FormLegend>
                    {{ form.medias.length }} / {{ props.maxMediaCount }}
                    visuel(s). Formats acceptés : JPG et PNG, 5 Mo maximum par
                    fichier. Glissez-déposez les vignettes pour définir l'ordre
                    de publication.
                </FormLegend>

                <FilePondField
                    v-model="form.medias"
                    field-name="medias"
                    field-id="medias"
                    accept="image/jpeg, image/png"
                    :max-files="remainingMediaCount"
                    max-file-size="5MB"
                    gallery
                    :initial-files="initialMedias"
                    :url-import-endpoint="mediaUrlImportEndpoint"
                    @processing="isUploading = $event"
                />
            </FormContent>
        </FormColumn>

        <FormFooter
            :is-submitting="isSubmitting"
            :errors="errors"
            :disabled="isUploading || !props.isPlatformConfigured"
            submit-text="Programmer"
            submitting-text="Enregistrement en cours..."
        >
            <template #actions>
                <ActionButton
                    type="secondary"
                    :disabled="
                        isSubmitting ||
                        isUploading ||
                        !props.isPlatformConfigured
                    "
                    @click="save('draft')"
                >
                    Enregistrer en brouillon
                </ActionButton>

                <template v-if="props.smoPost">
                    <ActionButton
                        type="secondary"
                        :left-icon="CaretLeftIcon"
                        :disabled="!props.previousSmoPostId || isSubmitting"
                        title="Publication précédente avec le même statut"
                        @click="goToSmoPost(props.previousSmoPostId)"
                    >
                        Précédent
                    </ActionButton>

                    <ActionButton
                        type="secondary"
                        :right-icon="CaretRightIcon"
                        :disabled="!props.nextSmoPostId || isSubmitting"
                        title="Publication suivante avec le même statut"
                        @click="goToSmoPost(props.nextSmoPostId)"
                    >
                        Suivant
                    </ActionButton>
                </template>
            </template>
        </FormFooter>
    </FormRoot>
</template>
